// Maps a DIAGNOSIS extraction payload onto the "Add New Diagnosis" form state.
//
// Kept out of DiagnosisManagement.js deliberately: this is the part that breaks
// if the upstream extraction schema moves (the backend is a pass-through and
// does not freeze it), so it is worth reading in one piece.
//
// Two rules drive everything here:
//   1. Almost every leaf is nullable, and whole sections can be null or []. A
//      missing value must leave the form's own default alone — never write
//      null/"" into a controlled input.
//   2. Master-backed dropdowns are only filled on an exact (case-insensitive)
//      match against the loaded master list, and we write the MASTER's casing,
//      never the AI's. Anything unmatched is reported, not forced.
//
// See docs/report-parser/api-clinical-extraction.md §4.3-§4.4 (revision 3).

import moment from "moment";

// Staging vocabularies. These mirror the option lists in PatientStagingDetails
// and are the canonical source for the T STAGE / N STAGE columns in
// DiagnosisManagement, so the two places that record T/N cannot drift apart.
export const T_STAGE_VALUES = [
  "0", "1", "1a", "1b", "1c",
  "2", "2a", "2b", "2c",
  "3", "3a", "3b", "3c",
  "4", "4a", "4b", "4c",
];

export const N_STAGE_VALUES = [
  "0", "1", "1a", "1b", "1c",
  "2", "2a", "2b", "2c",
  "3", "3a", "3b", "3c",
  "4", "4a", "4b", "4c",
];

const M_STAGE_VALUES = ["0", "1", "1a", "1b", "1c"];

const OVERALL_STAGE_VALUES = [
  "I", "IA", "IB", "IC",
  "II", "IIA", "IIB", "IIC",
  "III", "IIIA", "IIIB", "IIIC",
  "IV", "IVA", "IVB", "IVC",
];

// AJCC stage prefixes. `post_neoadjuvant` collapses yc/yp upstream and cannot
// be resolved here, so it maps to the bare modifier `y`. See §4.4.
const STAGING_TYPE_TO_PREFIX = {
  clinical: "c",
  pathological: "p",
  post_neoadjuvant: "y",
  restaging: "r",
};

// The form's radio values differ from the API enum by one apostrophe.
const METASTASIS_FLAG_TO_FORM = {
  yes: "Yes",
  no: "No",
  dontknow: "Don't Know",
};

const RISK_STRATIFICATION_VALUES = ["Low", "Intermediate", "High", "NotAvailable"];

// Product decision: an extracted Primary Site with no entry in the master list
// is recorded against this catch-all rather than left blank, so the field is
// never silently empty on an AI-populated form. Matched case-insensitively
// against the loaded master — if the backend has not defined it yet, we fall
// back to leaving the field blank rather than inventing a value.
export const PRIMARY_SITE_NOT_FOUND = "Not Found";

const MEASUREMENT_UNITS = ["mm", "cm"];

function isPresent(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function normalize(value) {
  return String(value).trim().toLowerCase();
}

// Returns the option as the master spells it, or null when there is no exact
// (case-insensitive, trimmed) match. Never returns the caller's own string.
function matchMaster(value, options) {
  if (!isPresent(value) || !Array.isArray(options)) return null;
  const wanted = normalize(value);
  for (let i = 0; i < options.length; i += 1) {
    const option = options[i];
    if (isPresent(option) && normalize(option) === wanted) return option;
  }
  return null;
}

// Like matchMaster, but for the primary-site master, whose entries are objects
// rather than plain names — the diagnosis form needs the siteId, not just the
// spelling, so it can send a link rather than a string.
function matchSite(value, options) {
  if (!isPresent(value) || !Array.isArray(options)) return null;
  const wanted = normalize(value);
  for (let i = 0; i < options.length; i += 1) {
    const option = options[i];
    if (option && isPresent(option.siteName) && normalize(option.siteName) === wanted) {
      return option;
    }
  }
  return null;
}

function matchExact(value, allowed) {
  if (!isPresent(value)) return null;
  const wanted = normalize(value);
  for (let i = 0; i < allowed.length; i += 1) {
    if (normalize(allowed[i]) === wanted) return allowed[i];
  }
  return null;
}

// "2.4 cm" -> { value: "2.4", unit: "cm" }. Units live inside the string
// upstream (§4.3 rule 5). An unrecognised or absent unit keeps the form's mm
// default rather than inventing one.
export function splitMeasurement(raw) {
  if (!isPresent(raw)) return null;
  const text = String(raw).trim();

  const numeric = text.match(/-?\d+(\.\d+)?/);
  if (!numeric) return null;

  let unit = "mm";
  const unitMatch = text.match(/[a-zA-Z]+/);
  if (unitMatch) {
    const candidate = normalize(unitMatch[0]);
    if (MEASUREMENT_UNITS.indexOf(candidate) !== -1) unit = candidate;
  }

  return { value: numeric[0], unit };
}

// "T2" -> "2", "N1a" -> "1a", "M0" -> "0". Returns null unless the remainder is
// in the target dropdown's vocabulary — a value the user could not otherwise
// have selected is dropped rather than written.
export function stripStagePrefix(raw, prefix, allowed) {
  if (!isPresent(raw)) return null;
  const text = String(raw).trim();
  const stripped = text.replace(new RegExp("^" + prefix, "i"), "");
  return matchExact(stripped, allowed);
}

export function mapStagingType(raw) {
  if (!isPresent(raw)) return null;
  return STAGING_TYPE_TO_PREFIX[normalize(raw)] || null;
}

// Dates arrive as date-only YYYY-MM-DD with no offset (§8.2), so parsing them
// with an explicit format keeps them on the calendar day the report printed.
function toMoment(raw) {
  if (!isPresent(raw)) return null;
  const parsed = moment(String(raw).trim(), "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed : null;
}

function section(result, key) {
  const value = result && result[key];
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function list(result, key) {
  const value = result && result[key];
  return Array.isArray(value) ? value : [];
}

/**
 * Builds a partial formData patch from an extraction result.
 *
 * @param {object} result   `data.result` from getExtractionResult
 * @param {object} masters  loaded dropdown masters. All are arrays of strings —
 *                          statuses, diagnosisTypes, histologies,
 *                          histologyGrades, biomarkerNames, biomarkerStatuses —
 *                          except `primarySites`, which is the raw master rows
 *                          ({ siteId, siteName, ... }) because the form links a
 *                          diagnosis to a site by id, not by spelling.
 * @returns {{ patch: object, metastaticSiteNames: string[],
 *             unmatched: string[], notes: string[], filledCount: number }}
 *
 * Mapped fields:
 *   - core_diagnosis.date_of_diagnosis → dateOfDiagnosis
 *   - core_diagnosis.status → status
 *   - core_diagnosis.diagnosis_type → diagnosisType
 *   - core_diagnosis.primary_site → primarySites
 *   - core_diagnosis.core_diagnosis → coreDiagnosis (canonical "description - ICD code")
 *   - core_diagnosis.tumor_group → tumorGroup
 *   - core_diagnosis.laterality → laterality
 *   - core_diagnosis.diagnosis_description → description
 *   - core_diagnosis.remarks → remarks
 *   - core_diagnosis.risk_stratification → riskStratification
 *   - histology.histology_description → histology
 *   - histology.grade_code → histologyGrade
 *   - metastasis.metastasis_flag → metastasis
 *   - metastasis.metastasis_details → metastasisDetails
 *   - metastasis.metastasis_date → metastasisDate
 *   - metastasis.metastatic_sites → metastaticSiteNames (for component handler)
 *   - biomarkers[].* → biomarkers
 *   - tumour_size.* → tumorSizes
 *   - lymph_nodes[].* → lymphNodes
 *   - tnm_staging.* → stagingDetails
 */
export function mapDiagnosisResultToForm(result, masters) {
  const patch = {};
  const unmatched = [];
  const notes = [];
  const metastaticSiteNames = [];
  const m = masters || {};

  if (!result || typeof result !== "object") {
    return { patch, metastaticSiteNames, unmatched, notes, filledCount: 0 };
  }

  // --- core_diagnosis ------------------------------------------------------
  const core = section(result, "core_diagnosis");

  const dateOfDiagnosis = toMoment(core.date_of_diagnosis);
  if (dateOfDiagnosis) patch.dateOfDiagnosis = dateOfDiagnosis;

  const status = matchMaster(core.status, m.statuses);
  if (status) patch.status = status;
  else if (isPresent(core.status)) unmatched.push("Status: " + core.status);

  const diagnosisType = matchMaster(core.diagnosis_type, m.diagnosisTypes);
  if (diagnosisType) patch.diagnosisType = diagnosisType;
  else if (isPresent(core.diagnosis_type)) {
    unmatched.push("Diagnosis Type: " + core.diagnosis_type);
  }

  // A diagnosis carries a LIST of primary sites, so this emits one even though
  // an extraction only ever yields a single core.primary_site — the clinician
  // adds any synchronous primaries by hand afterwards.
  const primarySite = matchSite(core.primary_site, m.primarySites);
  if (primarySite) {
    patch.primarySites = [{ siteId: primarySite.siteId, siteName: primarySite.siteName }];
  } else if (isPresent(core.primary_site)) {
    // No master match — record it against the catch-all if one exists, and
    // always surface what the report actually said. Mapping to "Not Found"
    // silently would hide a clinical detail the clinician needs to see.
    const catchAll = matchSite(PRIMARY_SITE_NOT_FOUND, m.primarySites);
    if (catchAll) {
      patch.primarySites = [{ siteId: catchAll.siteId, siteName: catchAll.siteName }];
      notes.push(
        'Primary Site "' + core.primary_site + '" is not in the list, so it has ' +
          'been set to "' + catchAll.siteName + '". Please correct it if there is a better match.'
      );
    } else {
      unmatched.push("Primary Site: " + core.primary_site);
    }
  }

  // Current Diagnosis mapping from core_diagnosis field (canonical master list entry)
  // Format: "<description> - <ICD code>" from the downstream cancer-site master list
  if (isPresent(core.core_diagnosis)) {
    patch.coreDiagnosis = String(core.core_diagnosis);
  }

  // Tumor group (classification grouping from the report)
  if (isPresent(core.tumor_group)) {
    patch.tumorGroup = String(core.tumor_group);
  }

  // Laterality of the primary site
  const lateralityValues = ["Left", "Right", "Bilateral", "Midline", "Not Applicable"];
  const laterality = matchExact(core.laterality, lateralityValues);
  if (laterality) patch.laterality = laterality;
  else if (isPresent(core.laterality)) unmatched.push("Laterality: " + core.laterality);

  if (isPresent(core.diagnosis_description)) {
    patch.description = String(core.diagnosis_description);
  }
  if (isPresent(core.remarks)) patch.remarks = String(core.remarks);

  const risk = matchExact(core.risk_stratification, RISK_STRATIFICATION_VALUES);
  if (risk) patch.riskStratification = risk;

  // --- histology -----------------------------------------------------------
  const histology = section(result, "histology");

  const histologyName = matchMaster(histology.histology_description, m.histologies);
  if (histologyName) patch.histology = histologyName;
  else if (isPresent(histology.histology_description)) {
    unmatched.push("Histology: " + histology.histology_description);
  }

  const gradeName = matchMaster(histology.grade_description, m.histologyGrades) ||
                    matchMaster(histology.grade_code, m.histologyGrades);
  if (gradeName) patch.histologyGrade = gradeName;
  else if (isPresent(histology.grade_description) || isPresent(histology.grade_code)) {
    unmatched.push("Histology Grade: " + (histology.grade_description || histology.grade_code));
  }

  // --- metastasis ----------------------------------------------------------
  const metastasis = section(result, "metastasis");

  const flag = isPresent(metastasis.metastasis_flag)
    ? METASTASIS_FLAG_TO_FORM[normalize(metastasis.metastasis_flag)]
    : null;
  if (flag) patch.metastasis = flag;

  if (isPresent(metastasis.metastasis_details)) {
    patch.metastasisDetails = String(metastasis.metastasis_details);
  }

  // Held as a moment: the save path calls .format() on it and there is no
  // rendered input to coerce it back from a string.
  const metastasisDate = toMoment(metastasis.metastasis_date);
  if (metastasisDate) patch.metastasisDate = metastasisDate;

  // Sites are applied through the component's own toggle handler, which owns
  // both selectedMetastaticSites and formData.metastaticSites.
  if (flag === "Yes") {
    list(metastasis, "metastatic_sites").forEach((site) => {
      const name = site && site.site_name;
      if (isPresent(name)) metastaticSiteNames.push(String(name).trim());
    });
  }

  // --- biomarkers ----------------------------------------------------------
  const biomarkerRows = [];
  list(result, "biomarkers").forEach((item) => {
    if (!item) return;
    const name = matchMaster(item.name, m.biomarkerNames);
    if (!name) {
      if (isPresent(item.name)) unmatched.push("Biomarker: " + item.name);
      return; // a row without a name is dropped by the save path anyway
    }
    biomarkerRows.push({
      name,
      status: matchMaster(item.status, m.biomarkerStatuses) || "",
      value: isPresent(item.value) ? String(item.value) : "",
    });
  });
  if (biomarkerRows.length > 0) patch.biomarkers = biomarkerRows;

  // --- tumour_size ---------------------------------------------------------
  const tumour = section(result, "tumour_size");
  const tnm = section(result, "tnm_staging");

  const tumourRow = {
    length: "", lengthUnit: "mm",
    width: "", widthUnit: "mm",
    depth: "", depthUnit: "mm",
    tStage: "",
    description: "",
  };
  let tumourFilled = false;

  [
    ["tumor_length", "length", "lengthUnit"],
    ["tumor_width", "width", "widthUnit"],
    ["tumor_depth", "depth", "depthUnit"],
  ].forEach(([apiKey, valueKey, unitKey]) => {
    const measurement = splitMeasurement(tumour[apiKey]);
    if (measurement) {
      tumourRow[valueKey] = measurement.value;
      tumourRow[unitKey] = measurement.unit;
      tumourFilled = true;
    }
  });

  if (isPresent(tumour.tumor_description)) {
    tumourRow.description = String(tumour.tumor_description);
    tumourFilled = true;
  }

  const tumourTStage = stripStagePrefix(tnm.t_stage, "T", T_STAGE_VALUES);
  if (tumourTStage) {
    tumourRow.tStage = tumourTStage;
    tumourFilled = true;
  }

  if (tumourFilled) patch.tumorSizes = [tumourRow];

  // --- lymph_nodes ---------------------------------------------------------
  const lymphNodeRows = [];
  list(result, "lymph_nodes").forEach((item) => {
    if (!item) return;
    const row = {
      lnGroup: isPresent(item.ln_group) ? String(item.ln_group) : "",
      length: "", lengthUnit: "mm",
      width: "", widthUnit: "mm",
      depth: "", depthUnit: "mm",
      nStage: stripStagePrefix(item.n_stage, "N", N_STAGE_VALUES) || "",
      description: isPresent(item.description) ? String(item.description) : "",
    };

    [
      ["length", "length", "lengthUnit"],
      ["width", "width", "widthUnit"],
      ["depth", "depth", "depthUnit"],
    ].forEach(([apiKey, valueKey, unitKey]) => {
      const measurement = splitMeasurement(item[apiKey]);
      if (measurement) {
        row[valueKey] = measurement.value;
        row[unitKey] = measurement.unit;
      }
    });

    const hasContent =
      row.lnGroup || row.length || row.width || row.depth || row.nStage || row.description;
    if (hasContent) lymphNodeRows.push(row);
  });
  if (lymphNodeRows.length > 0) patch.lymphNodes = lymphNodeRows;

  // --- tnm_staging ---------------------------------------------------------
  const stagingRow = {
    type: mapStagingType(tnm.staging_type) || "",
    t: stripStagePrefix(tnm.t_stage, "T", T_STAGE_VALUES) || "",
    n: stripStagePrefix(tnm.n_stage, "N", N_STAGE_VALUES) || "",
    m: stripStagePrefix(tnm.m_stage, "M", M_STAGE_VALUES) || "",
    stage: "",
    date: null,
    isSaved: false,
  };

  if (isPresent(tnm.staging_type) && !stagingRow.type) {
    unmatched.push("Staging Type: " + tnm.staging_type);
  }

  // A computed overall stage is the AI staging engine's derived opinion, not
  // something a pathologist wrote — and once saved that distinction is gone.
  // Fill it only when it was read from the report (§4.3).
  const stagingSource = section(result, "_pipeline_info").overall_staging_source;
  const overallStage = matchExact(tnm.overall_stage, OVERALL_STAGE_VALUES);
  if (overallStage) {
    if (isPresent(stagingSource) && normalize(stagingSource) === "extracted") {
      stagingRow.stage = overallStage;
    } else {
      notes.push(
        "Overall stage (" + overallStage + ") was calculated by the extraction " +
          "service rather than read from the report, so it has been left blank " +
          "— please set it yourself."
      );
    }
  }

  const stagingDate = toMoment(tnm.staging_date);
  if (stagingDate) stagingRow.date = stagingDate.format("DD-MM-YYYY");

  if (stagingRow.type || stagingRow.t || stagingRow.n || stagingRow.m ||
      stagingRow.stage || stagingRow.date) {
    patch.stagingDetails = [stagingRow];
  }

  const filledCount = Object.keys(patch).length + (metastaticSiteNames.length > 0 ? 1 : 0);

  return { patch, metastaticSiteNames, unmatched, notes, filledCount };
}
